import { useEffect, useMemo, useState } from 'react';
import { Map, Marker, useMap } from '@vis.gl/react-google-maps';
import { Button } from 'react-frontier';
import { Location } from '../SiteClasses';
import { formatAddress } from '../SiteUtil';

interface LocationMapProps{
  location?: Location | null;
  height?: number;
  zoom?: number;
  radius?: number | null;
  label?: string;
  showLabel?: boolean;
}

interface RadiusCircleProps{
  center: google.maps.LatLngLiteral;
  radius?: number | null;
}

var ZoomControls = ()=>{
  var map = useMap();
  if (!map) return null;

  var zoomIn = () => map.setZoom((map.getZoom() ?? 16) + 1);
  var zoomOut = () => map.setZoom((map.getZoom() ?? 16) - 1);

  return (
    <div className="zoom">
      <Button onClick={zoomIn} iconName="plus" color="white" size="tiny" />
      <Button onClick={zoomOut} iconName="minus" color="white" size="tiny" />
    </div>
  );
};

var RadiusCircle = ({ center, radius }: RadiusCircleProps): null =>{
  var map = useMap();

  useEffect(()=>{
    if(!map || !radius || radius <= 0) return;

    var circle = new google.maps.Circle({
      map,
      center,
      radius,
      clickable: false,
      strokeColor: '#FF6347',
      strokeOpacity: 0.45,
      strokeWeight: 1,
      fillColor: '#E37C42',
      fillOpacity: 0.12,
    });

    var bounds = circle.getBounds();
    if(bounds) map.fitBounds(bounds, 50);

    return ()=>{
      circle.setMap(null);
    };
  }, [map, center, radius]);

  return null;
};

var LocationMap = ({
  location,
  height = 220,
  zoom = 16,
  radius,
  label,
  showLabel = false,
}: LocationMapProps)=>{

  var center = useMemo(()=>{
    if (!location || typeof location.lattitude !== 'number' || typeof location.longitude !== 'number') return null;
    return { lat: location.lattitude, lng: location.longitude };
  }, [location?.lattitude, location?.longitude, location]);

  var [initialZoom] = useState(zoom);
  var normalizedRadius = useMemo(()=>{
    var parsedRadius = Number(radius);
    return Number.isFinite(parsedRadius) && parsedRadius > 0 ? parsedRadius : null;
  }, [radius]);

  if(!center) return null;

  var titleLine = (label || formatAddress(location)).trim();

  var lines = [
    [location.street, location.exterior_number, location.interior_number && `Int ${location.interior_number}`]
      .filter(Boolean)
      .join(' ')
      .trim(),
    location.neighborhood?.trim(),
    [location.city, location.state, location.zipcode].filter(Boolean).join(', ').trim(),
  ].filter(Boolean);

  var showCard = showLabel && (titleLine || lines.length);

  return (
    <div className="location-map">
      <div className="frame" style={{ height }}>
        {showCard && (
          <div className="floating-card">
            {titleLine && (
              <div className="floating-title" title={titleLine}>
                {titleLine}
              </div>
            )}
            {lines.map((line, i) => (
              <div key={i} className="floating-line">
                {line}
              </div>
            ))}
          </div>
        )}
        <Map
          className="map"
          style={{ width: '100%', height }}
          defaultCenter={center}
          center={center}
          defaultZoom={initialZoom}
          gestureHandling="greedy"
          scrollwheel={true}
          disableDefaultUI
        >
          <RadiusCircle center={center} radius={normalizedRadius} />
          <Marker position={center} />
          <ZoomControls />
        </Map>
      </div>
    </div>
  );
};

export default LocationMap;
